// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import Parallax, { PARALLAX_DEFAULT_SPEED } from 'containers/Parallax';
import Box from 'components/Box';
import Img, { getSrc } from 'components/Image';
import type { ImageData } from 'api/shared';
import AmpContext from 'utils/AmpContext';
import { retinaBackgroundImage } from 'utils/style';
import { imageUrl } from 'utils/filestack';
import { colors, breakpoints, gutterWidth } from '../theme';

const { useContext } = React;

const filestackTransform = (width: number, height: number) =>
  `resize=w:${width},h:${height}/quality=value:70`;

const BannerImage = styled(Img)`
  ${props =>
    props.$mobileImageHandle
      ? `
        display: none;
        @media (min-width: ${breakpoints[3]}) {
          display: block;
        }
      `
      : `
        display: block;
      `}
`;

const MobileBannerImage = styled(Img)`
  display: block;
  @media (min-width: ${breakpoints[3]}) {
    display: none;
  }
`;

const Banner = styled(
  ({
    ampEnabled,
    imageHandle,
    mobileImageHandle,
    alwaysShow,
    convert,
    parallax,
    ...props
  }) => (
    <Box {...props}>
      {mobileImageHandle && (
        <Box display={['block', null, null, 'none']} height="100%">
          <MobileBannerImage
            width={992}
            height={605}
            alt={getText('Hero image')}
            src={getSrc({
              handle: mobileImageHandle,
              width: 992,
              height: 605,
              format: convert ? 'jpg' : undefined
            })}
            srcSet={`${getSrc({
              handle: mobileImageHandle,
              width: 992,
              height: 605,
              format: convert ? 'jpg' : undefined
            })},
              ${getSrc({
                handle: mobileImageHandle,
                width: 1984,
                height: 1210,
                format: convert ? 'jpg' : undefined
              })} 2x`}
            alwaysShow={alwaysShow}
          />
        </Box>
      )}
      <BannerImage
        data-qa-id="hero-image-image"
        $mobileImageHandle={mobileImageHandle}
        width={1920}
        height={1024}
        alt={getText('Hero image')}
        src={getSrc({
          handle: imageHandle,
          width: 1920,
          height: 1024,
          format: convert ? 'jpg' : undefined
        })}
        srcSet={`${getSrc({
          handle: imageHandle,
          width: 1920,
          height: 1024,
          format: convert ? 'jpg' : undefined
        })},
          ${getSrc({
            handle: imageHandle,
            width: 2400,
            height: parallax ? 1600 : 1152,
            format: convert ? 'jpg' : undefined
          })} 2x`}
        alwaysShow={alwaysShow}
      />
    </Box>
  )
)`
  background-color: ${colors.battleshipGrey};
  background-size: cover;
  background-position: center;
  ${props =>
    props.mobileImageHandle || props.imageHandle
      ? retinaBackgroundImage(
          imageUrl({
            handle: props.mobileImageHandle || props.imageHandle,
            transform: filestackTransform(992, 605),
            format: props.convert ? 'jpg' : undefined
          }),
          {
            filename2x: imageUrl({
              handle: props.mobileImageHandle || props.imageHandle,
              transform: filestackTransform(1984, 1210),
              format: props.convert ? 'jpg' : undefined
            }),
            include3x: false
          }
        )
      : css`
          background-image: none;
        `}
  @media (min-width: ${breakpoints[2]}) {
    ${props =>
      props.imageHandle
        ? retinaBackgroundImage(
            imageUrl({
              handle: props.imageHandle,
              transform: filestackTransform(1920, 1024),
              format: props.convert ? 'jpg' : undefined
            }),
            {
              filename2x: imageUrl({
                handle: props.imageHandle,
                transform: filestackTransform(
                  2400,
                  props.parallax ? 1600 : 1152
                ),
                format: props.convert ? 'jpg' : undefined
              }),
              include3x: false
            }
          )
        : css`
            background-image: none;
          `}
  }
  img {
    display: none;
    height: 100%;
    width: 100%;
  }
  ${props =>
    props.ampEnabled
      ? ''
      : `
  @supports (object-fit: cover) {
    background-image: none;
    img {
      display: block;
      object-fit: cover;
    }
  }`}
`;

const Hero = ({
  heroImage,
  mobileHeroImage,
  children,
  withOverlay,
  withLightOverlay,
  parallax,
  alwaysShow,
  ...props
}: {
  heroImage: ?ImageData,
  mobileHeroImage?: ?ImageData,
  children: React.Node,
  withOverlay?: boolean,
  withLightOverlay?: boolean,
  'data-qa-id': ?string,
  parallax?: boolean,
  alwaysShow?: boolean
}) => {
  const ampEnabled = useContext(AmpContext);

  return (
    <Box
      position="relative"
      display="flex"
      width={1}
      minHeight={['320px', null, null, '450px']}
      px={[gutterWidth / 2, null, null, gutterWidth]}
      py={[48, null, null, 96]}
      justifyContent="center"
      alignItems="center"
      color="white"
      data-qa-id={props['data-qa-id']}
      {...props}
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        width={1}
        height="100%"
        overflow="hidden"
      >
        <Parallax
          y={[0, null]}
          width={1}
          height="100%"
          disabled={!parallax || ampEnabled}
        >
          {(heroImage || mobileHeroImage) && (
            <Banner
              position="absolute"
              transform="translateZ(0)"
              bottom={0}
              left={0}
              width={1}
              height={
                parallax ? `calc(100% + ${PARALLAX_DEFAULT_SPEED}px)` : '100%'
              }
              imageHandle={heroImage && heroImage.handle}
              convert={!(heroImage && heroImage.mimetype === 'image/jpeg')}
              mobileImageHandle={mobileHeroImage && mobileHeroImage.handle}
              data-qa-id="hero-image"
              ampEnabled={ampEnabled}
              alwaysShow={alwaysShow || parallax}
              parallax={parallax}
            />
          )}
        </Parallax>
        {(withOverlay || withLightOverlay) && (
          <Box
            position="absolute"
            transform="translateZ(0)"
            top={0}
            left={0}
            width={1}
            height="100%"
            bg={`rgba(0,0,0,${withLightOverlay ? 0.2 : 0.4})`}
          />
        )}
      </Box>
      <Box
        position="relative"
        transform="rotateZ(0)"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        width="100%"
      >
        {children}
      </Box>
    </Box>
  );
};

export default Hero;
